import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { conferenceService } from '../../../services/conferenceService';
import { formatDate } from '../../../utils/formatters';

const HeroSection = ({ conference, onSubmitClick, onNotifyClick }) => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('conferences');
  const [downloading, setDownloading] = useState(false);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (conference?.bannerImageUrl) {
      setImgLoaded(false);
      setImgError(false);
      conferenceService?.getBannerImageUrl(conference?.bannerImageUrl)?.then(url => {
        setBannerUrl(url);
      })?.catch(() => setBannerUrl(null));
    }
  }, [conference?.bannerImageUrl]);

  const showBanner = bannerUrl && !imgError;

  const handleDownloadCfp = async () => {
    if (!conference?.callForPapersUrl) {
      toastError('Call for papers document is not available');
      return;
    }

    try {
      setDownloading(true);
      const url = await conferenceService?.getCallForPapersPDFUrl(conference?.callForPapersUrl);
      if (url) {
        window.open(url, '_blank');
      }
    } catch (err) {
      toastError(`Error downloading CFP: ${err?.message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="border-b border-border">
      {showBanner && (
        <div className="w-full bg-muted">
          <img
            src={bannerUrl}
            alt={conference?.title}
            className={`w-full max-h-[400px] object-contain transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
          />
        </div>
      )}

      <div className="bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="space-y-4 md:space-y-5">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary text-xs md:text-sm font-medium caption">
                <Icon name="Calendar" size={14} />
                {conference?.type}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-accent/10 text-accent text-xs md:text-sm font-medium caption">
                <Icon name="MapPin" size={14} />
                {conference?.location}
              </span>
              {conference?.isVirtual && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-secondary/10 text-secondary text-xs md:text-sm font-medium caption">
                  <Icon name="Wifi" size={14} />
                  {t('conferenceLanding.virtualOption')}
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-foreground">
              {conference?.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm md:text-base text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={18} className="text-primary" />
                <span>{formatDate(conference?.startDate, { month: 'long' })} - {formatDate(conference?.endDate, { month: 'long' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={18} className="text-primary" />
                <span>{conference?.venue}</span>
              </div>
            </div>

            <p className="text-sm md:text-base lg:text-lg leading-relaxed max-w-3xl text-foreground/80">
              {conference?.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-2">
              <Button
                variant="default"
                size="lg"
                onClick={onSubmitClick}
                iconName="FileText"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                {t('conferenceLanding.submitYourPaper')}
              </Button>
              {conference?.callForPapersUrl && (
                <Button
                  variant="outline"
                  size="lg"
                  iconName={downloading ? 'Loader2' : 'Download'}
                  iconPosition="left"
                  className="w-full sm:w-auto"
                  onClick={handleDownloadCfp}
                  disabled={downloading}
                >
                  {t('conferenceLanding.downloadCfp')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;